import { useState, useEffect, useCallback } from "react";
import { useAppSelector, useAppDispatch } from "../../../redux/store";
import { useSnackbar } from "notistack";
import {
  Box,
  Grid,
  Typography,
  TextField,
  Paper,
  Button,
  InputLabel,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import LockResetIcon from "@mui/icons-material/LockReset";

import PaperWrapper from "../../../components/PaperWrapper";
import ProfilePicture from "./components/profilePicture";
import { PageHeader, Section as UiSection } from "../../../ui";
import ResetPasswordDialog from "./components/ResetPasswordDialog";
import {
  setEditing,
  setResetPasswordDialogOpen,
  resetErrors,
  resetSuccess,
} from "../../../redux/slices/profileSlice";
import { updateUser } from "../users/usersSlice";
import { updateUserData } from "../../../redux/slices/loginSlice";
import { openProjectDrawer } from "../../../redux/slices/projectsSlice";

// One labelled field, so the six of them on this page cannot drift apart.
//
// editable false shows the value as text rather than a greyed-out input: a
// client's name and phone are set by the office, and a disabled box invites
// someone to wonder why they cannot type in it.
const Field = ({
  id,
  label,
  value,
  onChange,
  disabled,
  editable = true,
  helperText,
}: {
  id: string;
  label: string;
  value: string;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  editable?: boolean;
  helperText?: string;
}) => (
  <Box>
    <InputLabel htmlFor={id} sx={{ fontSize: 13, mb: 0.5 }}>
      {label}
    </InputLabel>
    {editable ? (
      <TextField
        id={id}
        variant="outlined"
        size="small"
        fullWidth
        value={value}
        onChange={onChange}
        disabled={disabled}
        helperText={helperText}
        sx={{
          "& .MuiOutlinedInput-root": { borderRadius: 0 },
          "& .MuiOutlinedInput-input": { p: "8.5px 14px" },
        }}
      />
    ) : (
      <Typography variant="body1" sx={{ fontSize: 14, py: "8.5px" }}>
        {value || "—"}
      </Typography>
    )}
  </Box>
);

// A card for one group of settings.
const Section = ({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
}) => (
  <PaperWrapper>
    <Paper elevation={0} sx={{ p: { xs: 2, md: 3 }, height: "100%" }}>
      <UiSection title={title} hint={description}>
        {children}
      </UiSection>
    </Paper>
  </PaperWrapper>
);

const AccountSettings = () => {
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const { user } = useAppSelector((state) => state.login);
  const { personalInfo, contactInfo, loading, error, updateSuccess, isEditing } =
    useAppSelector((state) => state.profile);
  const { projects, fetchProjectsStatus } = useAppSelector(
    (state) => state.projects
  );

  const [localPersonalInfo, setLocalPersonalInfo] = useState({
    firstName: "",
    middleName: "",
    lastName: "",
    position: "",
  });
  const [localContactInfo, setLocalContactInfo] = useState({
    email: "",
    phoneNumber: "",
  });

  // Utility function to split full name
  const splitFullName = (fullName: string | undefined) => {
    if (!fullName) return { firstName: "", middleName: "", lastName: "" };

    const parts = fullName.trim().split(/\s+/);
    let firstName = parts[0] || "";
    let middleName = "";
    let lastName = "";

    if (parts.length === 2) {
      lastName = parts[1];
    } else if (parts.length >= 3) {
      middleName = parts.slice(1, -1).join(" ");
      lastName = parts[parts.length - 1];
    }

    return { firstName, middleName, lastName };
  };

  // Show what the account holds whenever the page is not being edited.
  useEffect(() => {
    if (user && !isEditing) {
      const { firstName, middleName, lastName } = splitFullName(user.username);
      setLocalPersonalInfo({
        firstName: personalInfo.firstName || firstName,
        middleName: personalInfo.middleName || middleName,
        lastName: personalInfo.lastName || lastName,
        position: personalInfo.position || user.position || user.role || "",
      });
      setLocalContactInfo({
        email: contactInfo.email || user.email || "",
        phoneNumber: contactInfo.phoneNumber || "",
      });
    }
  }, [user, personalInfo, contactInfo, isEditing]);

  useEffect(() => {
    if (updateSuccess) {
      enqueueSnackbar("Profile updated successfully", { variant: "success" });
      // Leave the form: the save is done, and the fields now show what was
      // saved rather than staying editable behind a button that already ran.
      dispatch(setEditing(false));
      // And let the rest of the portal — the sidebar footer, the toolbar — show
      // the new name and position without waiting for a reload.
      dispatch(
        updateUserData({
          username: [
            localPersonalInfo.firstName,
            localPersonalInfo.middleName,
            localPersonalInfo.lastName,
          ]
            .filter(Boolean)
            .join(" "),
          position: localPersonalInfo.position,
        })
      );
      dispatch(resetSuccess());
    }
  }, [
    updateSuccess,
    enqueueSnackbar,
    dispatch,
    localPersonalInfo.firstName,
    localPersonalInfo.middleName,
    localPersonalInfo.lastName,
    localPersonalInfo.position,
  ]);

  useEffect(() => {
    if (error) {
      enqueueSnackbar(`Error: ${error}`, { variant: "error" });
      dispatch(resetErrors());
    }
  }, [error, enqueueSnackbar, dispatch]);

  const handlePersonalInfoChange = useCallback(
    (field: keyof typeof localPersonalInfo) =>
      (event: React.ChangeEvent<HTMLInputElement>) => {
        setLocalPersonalInfo((prev) => ({ ...prev, [field]: event.target.value }));
      },
    []
  );

  const handleContactInfoChange = useCallback(
    (field: keyof typeof localContactInfo) =>
      (event: React.ChangeEvent<HTMLInputElement>) => {
        setLocalContactInfo((prev) => ({ ...prev, [field]: event.target.value }));
      },
    []
  );

  const handleEditToggle = useCallback(() => {
    dispatch(setEditing(!isEditing));
  }, [dispatch, isEditing]);

  const handleSave = useCallback(() => {
    if (!user?.id) {
      enqueueSnackbar("User ID not found", { variant: "error" });
      return;
    }

    dispatch(
      updateUser({
        id: user.id,
        firstName: localPersonalInfo.firstName,
        middleName: localPersonalInfo.middleName,
        lastName: localPersonalInfo.lastName,
        // No position: it is not editable here, and sending it would risk
        // writing the fallback shown on screen — the role — into the record.
        phoneNumber: localContactInfo.phoneNumber,
        email: localContactInfo.email,
        username: user.username,
        role: user.role,
      })
    );
  }, [dispatch, user, localPersonalInfo, localContactInfo, enqueueSnackbar]);

  const handleCancel = useCallback(() => {
    // Put back what the account actually holds.
    if (user) {
      const { firstName, middleName, lastName } = splitFullName(user.username);
      setLocalPersonalInfo({
        firstName: personalInfo.firstName || firstName,
        middleName: personalInfo.middleName || middleName,
        lastName: personalInfo.lastName || lastName,
        position: personalInfo.position || user.position || user.role || "",
      });
      setLocalContactInfo({
        email: contactInfo.email || user.email || "",
        phoneNumber: contactInfo.phoneNumber || "",
      });
    }
    dispatch(setEditing(false));
  }, [dispatch, user, personalInfo, contactInfo]);

  const handleResetPassword = useCallback(() => {
    dispatch(setResetPasswordDialogOpen(true));
  }, [dispatch]);

  const fullName = [
    localPersonalInfo.firstName,
    localPersonalInfo.middleName,
    localPersonalInfo.lastName,
  ]
    .filter(Boolean)
    .join(" ");

  // Who may change their own details. A client's name, position and phone are
  // the office's record of them, not theirs to edit — they get their password
  // and their picture, which is what this page is for them.
  const canEditProfile = ["super-admin", "admin", "employee"].includes(
    user?.role ?? ""
  );

  // The API already masks stored accounts, but only the last four digits are
  // ever shown here even if a full number comes back.
  const maskAccount = (value: unknown) => {
    const digits = String(value || "").replace(/\D/g, "");
    return digits.length >= 4 ? `•••• ${digits.slice(-4)}` : "Account on file";
  };

  const accountType = (account: any) =>
    `${account?.isSavings ? "Savings" : "Checking"} · ${
      account?.isPersonalAcct ? "Personal" : "Business"
    }`;

  return (
    <Box sx={{ maxWidth: 1100, mx: "auto", pb: 4 }}>
      <PageHeader
        eyebrow="Account"
        title="My Settings"
        summary="Your details, the password the portal asks for, and how your name appears to everyone else."
      />
      {/* Who the account belongs to, and the one action that changes any of it. */}
      <PaperWrapper>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, md: 3 },
            mb: 3,
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "flex-start", sm: "center" },
            gap: 2.5,
          }}
        >
          <ProfilePicture size={96} />

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h5" fontWeight="bold" sx={{ mb: 0.5 }}>
              {fullName || user?.username || "Your account"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {localPersonalInfo.position || user?.role || ""}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ wordBreak: "break-word" }}>
              {user?.email}
            </Typography>
          </Box>

          {/* A Box rather than a Stack: the dashboard paints its hero image
              across any Stack inside a Stack, and this page is a "cabinet",
              which paints it 250px tall with a 216px inset. */}
          {canEditProfile && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                flexShrink: 0,
                alignSelf: { xs: "stretch", sm: "center" },
                justifyContent: { xs: "flex-start", sm: "flex-end" },
              }}
            >
              {isEditing ? (
                <>
                  <Button
                    size="small"
                    onClick={handleCancel}
                    disabled={loading}
                    sx={{ borderRadius: 0, }}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={handleSave}
                    disabled={loading}
                    sx={{ borderRadius: 0, }}
                  >
                    {loading ? "Saving…" : "Save changes"}
                  </Button>
                </>
              ) : (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<EditIcon />}
                  onClick={handleEditToggle}
                  sx={{ borderRadius: 0, }}
                >
                  Edit profile
                </Button>
              )}
            </Box>
          )}
        </Paper>
      </PaperWrapper>

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Section
            title="Personal information"
            description={
              canEditProfile
                ? "How your name appears to the rest of the portal."
                : "Kept by our office. Ask an administrator to change any of it."
            }
          >
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Field
                  editable={canEditProfile}
                  id="firstName"
                  label="First name"
                  value={localPersonalInfo.firstName}
                  onChange={handlePersonalInfoChange("firstName")}
                  disabled={!isEditing || loading}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Field
                  editable={canEditProfile}
                  id="middleName"
                  label="Middle name"
                  value={localPersonalInfo.middleName}
                  onChange={handlePersonalInfoChange("middleName")}
                  disabled={!isEditing || loading}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Field
                  editable={canEditProfile}
                  id="lastName"
                  label="Last name"
                  value={localPersonalInfo.lastName}
                  onChange={handlePersonalInfoChange("lastName")}
                  disabled={!isEditing || loading}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                {/* Never editable, by anyone, including an administrator:
                    a position is the office's record of what someone does. The
                    API refuses a change to one's own position as well, so this
                    is not the only thing holding the rule up. */}
                <Field
                  editable={false}
                  id="position"
                  label="Position"
                  value={localPersonalInfo.position}
                />
              </Grid>
            </Grid>
          </Section>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Section
            title="Contact details"
            description={
              canEditProfile
                ? "A phone number we can reach you on."
                : "Kept by our office. Ask an administrator to change it."
            }
          >
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Field
                  editable={canEditProfile}
                  id="phoneNumber"
                  label="Phone number"
                  value={localContactInfo.phoneNumber}
                  onChange={handleContactInfoChange("phoneNumber")}
                  disabled={!isEditing || loading}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                {/* Not editable here: it is the address the account signs in
                    with, so it is changed deliberately rather than by typing. */}
                <Field
                  id="email"
                  label="Email address"
                  value={localContactInfo.email}
                  disabled
                  helperText="The address you sign in with. Ask an administrator to change it."
                />
              </Grid>
            </Grid>
          </Section>
        </Grid>

        {user?.role === "client" &&
          (fetchProjectsStatus === "loading" && !projects?.length ? (
            <Grid size={{ xs: 12 }}>
              <Section
                title="Bank account"
                description="The account your project payments are drafted from."
              >
                <Typography variant="body2" color="text.secondary">
                  Loading your project accounts…
                </Typography>
              </Section>
            </Grid>
          ) : fetchProjectsStatus === "failed" && !projects?.length ? (
            <Grid size={{ xs: 12 }}>
              <Section
                title="Bank account"
                description="The account your project payments are drafted from."
              >
                <Typography variant="body2" color="text.secondary">
                  We could not load your projects. Refresh the page and try again.
                </Typography>
              </Section>
            </Grid>
          ) : projects?.length ? (
            projects.map((project: any, projectIndex: number) => {
              const accounts = Array.isArray(project?.bankAccounts)
                ? project.bankAccounts
                : [];
              return (
                <Grid
                  key={project?.id || project?._id || projectIndex}
                  size={{ xs: 12, md: projects.length > 1 ? 6 : 12 }}
                >
                  <Section
                    title={project?.name || "Project"}
                    description="The account payments for this project are drafted from."
                  >
                    {accounts.length ? (
                      accounts.map((account: any, accountIndex: number) => (
                        <Box
                          key={account?.accountID || `${projectIndex}-${accountIndex}`}
                          sx={{ py: 1, borderBottom: "1px solid #eeeeee" }}
                        >
                          <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                            {account?.accountName || "Bank account"}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {maskAccount(account?.accountNumber)} ·{" "}
                            {accountType(account)}
                          </Typography>
                          {account?.addedAt && (
                            <Typography variant="caption" color="text.secondary">
                              Added{" "}
                              {new Date(account.addedAt).toLocaleDateString(
                                "en-US",
                                { month: "short", day: "numeric", year: "numeric" }
                              )}
                            </Typography>
                          )}
                        </Box>
                      ))
                    ) : (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ py: 1 }}
                      >
                        No bank account linked. Add one to make payments.
                      </Typography>
                    )}
                    <Button
                      variant={accounts.length ? "outlined" : "contained"}
                      size="small"
                      sx={{ mt: 1.5, borderRadius: 0 }}
                      onClick={() => dispatch(openProjectDrawer(project))}
                    >
                      {accounts.length ? "Manage bank account" : "Add bank account"}
                    </Button>
                  </Section>
                </Grid>
              );
            })
          ) : (
            <Grid size={{ xs: 12 }}>
              <Section
                title="Bank account"
                description="The account your project payments are drafted from."
              >
                <Typography variant="body2" color="text.secondary">
                  No projects are linked to your account yet.
                </Typography>
              </Section>
            </Grid>
          ))}

        <Grid size={{ xs: 12 }}>
          <Section
            title="Password"
            description="Choose a password of your own; it is what the portal asks for when you sign in."
          >
            <Button
              variant="outlined"
              startIcon={<LockResetIcon />}
              onClick={handleResetPassword}
              sx={{ borderRadius: 0, }}
            >
              Change password
            </Button>
          </Section>
        </Grid>
      </Grid>

      <ResetPasswordDialog />
    </Box>
  );
};

export default AccountSettings;
